import { useState } from 'react'
import { formatDistanceToNow, format } from 'date-fns'
import { type ColumnDef } from '@tanstack/react-table'
import { Bug } from 'lucide-react'
import { useAdminQaFeedbackList } from '@/hooks/useAdminQaFeedback'
import { type QaFeedbackRow } from '@/lib/api/admin-qa-feedback'
import { DataTable } from '@/components/shared/DataTable'
import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

const completedStyles: Record<string, string> = {
  yes: 'bg-green-100 text-green-800 border-green-200',
  partially: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  no: 'bg-red-100 text-red-800 border-red-200',
}

function CompletedBadge({ value }: { value: QaFeedbackRow['completed'] }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <Badge
      variant="outline"
      className={cn('text-[10px] font-medium capitalize', completedStyles[value])}
    >
      {value}
    </Badge>
  )
}

function ScoreCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>
  return <span className="text-sm tabular-nums">{value}/5</span>
}

const columns: ColumnDef<QaFeedbackRow, unknown>[] = [
  {
    accessorKey: 'created_at',
    header: 'When',
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true })}
      </span>
    ),
    size: 130,
  },
  {
    id: 'tester',
    header: 'Tester',
    cell: ({ row }) => (
      <div className="min-w-0 flex items-center gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {row.original.tester_name ?? row.original.tester_email ?? '—'}
          </div>
          {row.original.tester_name && row.original.tester_email && (
            <div className="text-xs text-muted-foreground truncate">{row.original.tester_email}</div>
          )}
        </div>
        {row.original.is_test === true && (
          <Badge variant="secondary" className="text-[10px] shrink-0">
            Internal
          </Badge>
        )}
      </div>
    ),
    size: 220,
  },
  {
    accessorKey: 'section',
    header: 'Section',
    cell: ({ row }) => (
      <span className="text-sm font-mono">{row.original.section}</span>
    ),
    size: 180,
  },
  {
    accessorKey: 'completed',
    header: 'Completed',
    cell: ({ row }) => <CompletedBadge value={row.original.completed} />,
    size: 110,
  },
  {
    accessorKey: 'clarity_score',
    header: 'Clarity',
    cell: ({ row }) => <ScoreCell value={row.original.clarity_score} />,
    size: 80,
  },
  {
    accessorKey: 'ease_score',
    header: 'Ease',
    cell: ({ row }) => <ScoreCell value={row.original.ease_score} />,
    size: 80,
  },
  {
    id: 'bug',
    header: 'Bug',
    cell: ({ row }) =>
      row.original.bugs_encountered ? (
        <Bug className="h-4 w-4 text-red-600" aria-label="bug reported" />
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    size: 60,
  },
]

function DetailSection({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-sm whitespace-pre-wrap break-words">{value}</div>
    </div>
  )
}

export function QaFeedbackListPage() {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<QaFeedbackRow | null>(null)

  const { data, isLoading } = useAdminQaFeedbackList({ page: 1, pageSize: 100 })

  const filtered = (data?.entries ?? []).filter((e) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      e.section?.toLowerCase().includes(q) ||
      e.tester_email?.toLowerCase().includes(q) ||
      e.tester_name?.toLowerCase().includes(q) ||
      e.confusion_points?.toLowerCase().includes(q) ||
      e.improvement_ideas?.toLowerCase().includes(q) ||
      e.bugs_encountered?.toLowerCase().includes(q) ||
      e.specific_answer?.toLowerCase().includes(q)
    )
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="QA Feedback"
        description="Structured QA pass entries from public.qa_feedback. Read-only."
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        emptyMessage="No QA feedback entries yet."
        onRowClick={(row) => setSelected(row)}
        toolbar={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search section, tester, or text..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm h-9"
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        }
      />

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="font-mono text-base">{selected.section}</span>
                  {selected.is_test === true && (
                    <Badge variant="secondary" className="text-[10px]">
                      Internal
                    </Badge>
                  )}
                </SheetTitle>
                <SheetDescription>
                  {selected.tester_name ?? selected.tester_email ?? 'Unknown tester'} ·{' '}
                  {format(new Date(selected.created_at), 'PPpp')}
                </SheetDescription>
              </SheetHeader>

              <div className="px-6 pb-6 space-y-5">
                <div className="flex flex-wrap gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Completed:</span>{' '}
                    <CompletedBadge value={selected.completed} />
                  </div>
                  <div>
                    <span className="text-muted-foreground">Clarity:</span>{' '}
                    <ScoreCell value={selected.clarity_score} />
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ease:</span>{' '}
                    <ScoreCell value={selected.ease_score} />
                  </div>
                </div>

                <DetailSection label="Confusion points" value={selected.confusion_points} />
                <DetailSection label="Improvement ideas" value={selected.improvement_ideas} />
                <DetailSection label="Bugs encountered" value={selected.bugs_encountered} />
                <DetailSection label="Specific answer" value={selected.specific_answer} />

                <div className="pt-3 border-t text-xs text-muted-foreground space-y-1">
                  {selected.browser && <div>Browser: {selected.browser}</div>}
                  {selected.screen_size && <div>Screen: {selected.screen_size}</div>}
                  {selected.session_id && <div>Session: {selected.session_id}</div>}
                  {selected.user_id && <div className="font-mono">User: {selected.user_id}</div>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
